// Tarjeta flotante de un solo aviso (captacion-devocional-y-boletin.md, Fase 2). Montada en
// Layout.js, que decide en qué rutas puede existir (solo públicas, sin sesión, fuera de la
// app nativa).
//
// Reglas, todas de la investigación del 2026-09-28 (NN/g, Google Search Central):
//  - Nunca al llegar. Aparece al pasar la mitad de la página o en la segunda página vista de
//    la visita — cuando la persona ya decidió leer.
//  - No tapa el contenido: tarjeta chica abajo, encima de la tab bar en móvil (no se suma a
//    ella ni la tapa), esquina inferior derecha en escritorio. Nada de modal ni fondo oscuro:
//    Google penaliza en móvil las capas que tapan el contenido.
//  - En móvil arranca como una línea delgada sobre la tab bar (Víctor 2026-09-30: la tarjeta
//    completa tapaba el contenido) y se abre al tocar "Quiero recibirlo". Escritorio: tarjeta.
//  - Una vez por visita. "No, gracias" o la X la apagan 30 días.
//  - No aparece encima de un bloque de invitación que ya está en pantalla
//    (`[data-subscribe-invite]`): serían dos pedidos a la vez.
//  - A quien ya se inscribió o tiene pendiente la confirmación no se le muestra.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SubscribeForm from "./SubscribeForm";
import { useSubscriptionState } from "../../hooks/useSubscriptionState";

const SHOWN_KEY = "vde_subscribe_card_shown";
const VIEWS_KEY = "vde_page_views";

function sessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function sessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* almacenamiento bloqueado: la tarjeta puede volver a salir en esta visita, nada más */
  }
}

function inviteOnScreen() {
  const vh = window.innerHeight;
  return Array.from(document.querySelectorAll("[data-subscribe-invite]")).some((el) => {
    const r = el.getBoundingClientRect();
    return r.top < vh && r.bottom > 0;
  });
}

export default function SubscribeCard({ pathname, playerActive = false }) {
  const { t } = useTranslation();
  const [subState, setSubState] = useSubscriptionState();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const eligible = subState === "visitor";

  // Cuenta páginas vistas en la visita (una por ruta).
  useEffect(() => {
    const n = parseInt(sessionGet(VIEWS_KEY) || "0", 10) + 1;
    sessionSet(VIEWS_KEY, String(n));
  }, [pathname]);

  useEffect(() => {
    if (!eligible || open || sessionGet(SHOWN_KEY)) return undefined;

    const tryOpen = () => {
      if (sessionGet(SHOWN_KEY) || inviteOnScreen()) return false;
      sessionSet(SHOWN_KEY, "1");
      setOpen(true);
      return true;
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const secondPage = parseInt(sessionGet(VIEWS_KEY) || "0", 10) >= 2;
      if ((scrollable > 0 && window.scrollY / scrollable >= 0.5) || (secondPage && window.scrollY > 200)) {
        if (tryOpen()) window.removeEventListener("scroll", onScroll);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [eligible, open, pathname]);

  // Si en otra parte de la página la persona se inscribe, la tarjeta se va sola.
  useEffect(() => {
    if (open && subState === "subscribed") setOpen(false);
  }, [open, subState]);

  if (!open) return null;

  const dismiss = () => {
    setOpen(false);
    if (subState === "visitor") setSubState("dismissed");
  };

  // Móvil: encima de la tab bar (y del reproductor si está sonando algo). Escritorio: esquina.
  const mobileBottom = playerActive
    ? "bottom-[calc(11.5rem+env(safe-area-inset-bottom))]"
    : "bottom-[calc(5.75rem+env(safe-area-inset-bottom))]";

  return (
    <>
    {!expanded && (
      <div
        role="complementary"
        aria-label={t("subscribe.cardTitle")}
        className={`md:hidden animate-fade-in-up print:hidden fixed z-40 left-0 right-0 ${mobileBottom} flex items-center gap-3 pl-4 pr-2 py-2.5 bg-brand-parchment dark:bg-brand-dark-card border-t border-brand-gold/30 dark:border-brand-forest shadow-[0_-6px_16px_-8px_rgba(15,35,24,0.25)]`}
      >
        <p className="flex-1 min-w-0 text-sm font-black tracking-tight text-brand-forest dark:text-brand-parchment truncate">
          {t("subscribe.kicker")}
        </p>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex-shrink-0 bg-brand-gold hover:bg-brand-gold-dark text-brand-forest text-xs font-bold px-3.5 py-2 rounded-full transition-colors"
        >
          {t("subscribe.submit")}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("subscribe.close")}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-brand-forest/60 hover:text-brand-forest dark:text-brand-parchment/60"
        >
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    )}
    <div
      role="complementary"
      aria-label={t("subscribe.cardTitle")}
      className={`${expanded ? "" : "hidden md:block"} animate-fade-in-up print:hidden fixed z-40 left-3 right-3 ${mobileBottom} md:left-auto md:right-6 md:bottom-6 md:w-[380px] bg-brand-parchment dark:bg-brand-dark-card border border-brand-gold/25 dark:border-brand-forest rounded-2xl shadow-[0_10px_30px_-8px_rgba(33,29,22,0.35)] p-5`}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("subscribe.close")}
        className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-full text-brand-forest/60 hover:text-brand-forest hover:bg-brand-forest/5 dark:text-brand-parchment/60 dark:hover:text-brand-parchment transition-colors"
      >
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5" aria-hidden="true">
          <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
        </svg>
      </button>
      {subState !== "pending" && (
        <div className="pr-8 mb-3">
          <p className="font-black text-lg tracking-tight text-brand-forest dark:text-brand-parchment leading-tight">
            {t("subscribe.cardTitle")}
          </p>
          <p className="text-sm text-brand-forest/75 dark:text-brand-parchment/70 mt-1">{t("subscribe.cardBody")}</p>
        </div>
      )}
      <SubscribeForm source="card" cta="forest" compact align="left" />
      {subState === "visitor" && (
        <button
          type="button"
          onClick={dismiss}
          className="mt-3 text-xs font-semibold text-brand-forest/60 dark:text-brand-parchment/55 hover:text-brand-forest dark:hover:text-brand-parchment underline underline-offset-4"
        >
          {t("subscribe.dismiss")}
        </button>
      )}
    </div>
    </>
  );
}
